/**
 * Card encoding.
 *
 * A card is a plain integer 0..51:
 *
 *     card = (rank - 2) * 4 + suit
 *     rank = (card >> 2) + 2      // 2..14, where 11=J 12=Q 13=K 14=A
 *     suit = card & 3             // 0=♠ 1=♥ 2=♦ 3=♣
 *
 * Integers, not objects: the evaluator runs tens of millions of times in the
 * test suite alone, and boxing every card would dominate the cost. It also
 * makes a deck a plain `number[]` that serialises for free in hand events.
 *
 * This encoding is shared with the site's existing equity tool, so hands
 * recorded by either side are directly comparable.
 */

/** A card, 0..51. */
export type Card = number;

/** A rank, 2..14 (14 = ace). Aces are high here; the wheel is handled in the evaluator. */
export type Rank = number;

/** A suit, 0..3. */
export type Suit = 0 | 1 | 2 | 3;

export const SPADES = 0;
export const HEARTS = 1;
export const DIAMONDS = 2;
export const CLUBS = 3;

export const MIN_RANK = 2;
export const MAX_RANK = 14;
export const DECK_SIZE = 52;

/** Rank characters indexed by `rank - 2`. */
export const RANK_CHARS = ['2', '3', '4', '5', '6', '7', '8', '9', 'T', 'J', 'Q', 'K', 'A'] as const;

/** Suit characters indexed by suit. Order matches the numeric encoding. */
export const SUIT_CHARS = ['s', 'h', 'd', 'c'] as const;

/** Suit symbols indexed by suit, for display. */
export const SUIT_SYMBOLS = ['♠', '♥', '♦', '♣'] as const;

export function rankOf(card: Card): Rank {
  return (card >> 2) + 2;
}

export function suitOf(card: Card): Suit {
  return (card & 3) as Suit;
}

export function makeCard(rank: Rank, suit: Suit): Card {
  return (rank - 2) * 4 + suit;
}

/** True for hearts and diamonds — the ranks drawn in red. */
export function isRed(suit: Suit): boolean {
  return suit === HEARTS || suit === DIAMONDS;
}

/** `0` -> `"2s"`, `51` -> `"Ac"`. */
export function cardToString(card: Card): string {
  return RANK_CHARS[rankOf(card) - 2]! + SUIT_CHARS[suitOf(card)]!;
}

/** `"Ac"` -> `51`. Case-insensitive on the rank, strict on length. Throws on anything else. */
export function cardFromString(text: string): Card {
  if (text.length !== 2) {
    throw new Error(`invalid card ${JSON.stringify(text)}: expected 2 characters`);
  }
  const rankIndex = RANK_CHARS.indexOf(text[0]!.toUpperCase() as (typeof RANK_CHARS)[number]);
  if (rankIndex < 0) {
    throw new Error(`invalid card ${JSON.stringify(text)}: unknown rank ${JSON.stringify(text[0])}`);
  }
  const suit = SUIT_CHARS.indexOf(text[1]! as (typeof SUIT_CHARS)[number]);
  if (suit < 0) {
    throw new Error(`invalid card ${JSON.stringify(text)}: unknown suit ${JSON.stringify(text[1])}`);
  }
  return rankIndex * 4 + suit;
}

/** `"AcKd"` or `"Ac Kd"` -> `[51, 46]`. Whitespace between cards is optional. */
export function cardsFromString(text: string): Card[] {
  const compact = text.replace(/\s+/g, '');
  if (compact.length % 2 !== 0) {
    throw new Error(`invalid card list ${JSON.stringify(text)}: odd number of characters`);
  }
  const cards: Card[] = [];
  for (let i = 0; i < compact.length; i += 2) {
    cards.push(cardFromString(compact.slice(i, i + 2)));
  }
  return cards;
}

export function cardsToString(cards: readonly Card[]): string {
  return cards.map(cardToString).join(' ');
}

/**
 * A fresh ordered deck, `[0, 1, ..., 51]`.
 *
 * Deliberately unshuffled: the engine never generates randomness. Shuffling
 * belongs to the RNG module, which the runtime seeds and commits to.
 */
export function orderedDeck(): Card[] {
  const deck: Card[] = new Array(DECK_SIZE);
  for (let c = 0; c < DECK_SIZE; c++) deck[c] = c;
  return deck;
}

/** True if `card` is a valid encoding. Used at trust boundaries, not in hot paths. */
export function isValidCard(card: unknown): card is Card {
  return Number.isInteger(card) && (card as number) >= 0 && (card as number) < DECK_SIZE;
}
